import { readSelection, selectionKey, writeSelection } from './selection.js';


const positive = (value) => Number.isSafeInteger(Number(value)) && Number(value) > 0;
const named = (value) => typeof value?.name === 'string' && value.name.trim().length > 0;
const identifiedShop = (value) =>
  positive(value?.id) &&
  named(value) &&
  ['bayhive', 'autoflow', 'unclassified'].includes(value.ownership) &&
  typeof value.enabled === 'boolean';

function serverSelection(context, url) {
  // A provider reference catalog has no location ownership or account filter.
  if (context.scope === 'global') return { type: 'all', name: 'Global catalog' };
  const target =
    context.scope === 'account' && identifiedShop(context.target) ? context.target : null;
  const fallback =
    context.selection ||
    (target
      ? { ...target, type: 'shop' }
      : context.scope === 'account' && context.target
        ? { type: 'invalid' }
        : { type: 'all' });
  const requested = readSelection(url, fallback);
  if (context.selection && selectionKey(context.selection) !== selectionKey(requested))
    return { type: 'invalid' };
  if (requested.type === 'all') return { type: 'all', name: 'All accounts' };
  const supplied = context.selection || (target ? { ...target, type: 'shop' } : null);
  if (!supplied || selectionKey(supplied) !== selectionKey(requested)) return { type: 'invalid' };
  if (supplied.type === 'shop' && identifiedShop(supplied)) {
    if (
      context.scope === 'account' &&
      context.target &&
      (!identifiedShop(context.target) ||
        Number(context.target.id) !== Number(supplied.id) ||
        context.target.ownership !== supplied.ownership ||
        context.target.enabled !== supplied.enabled)
    )
      return { type: 'invalid' };
    return {
      type: 'shop',
      id: Number(supplied.id),
      name: supplied.name,
      ownership: supplied.ownership,
      enabled: supplied.enabled,
    };
  }
  if (
    supplied.type === 'mso' &&
    positive(supplied.id) &&
    named(supplied) &&
    Number.isInteger(supplied.locationCount) &&
    supplied.locationCount >= 0
  )
    return {
      type: 'mso',
      id: Number(supplied.id),
      name: supplied.name,
      locationCount: supplied.locationCount,
    };
  return { type: 'invalid' };
}

export function resolveAdminContext(page) {
  const context = page.props.adminContext || null;
  if (!context) return null;
  const url = new URL(page.url || window.location.href, window.location.origin);
  const returnId = Number(url.searchParams.get('admin_return_mso'));
  const returnMsoId = positive(returnId) ? returnId : null;
  const selection = serverSelection(context, url);
  const target =
    context.scope === 'account' && selection.type === 'shop'
      ? {
          id: selection.id,
          name: selection.name,
          ownership: selection.ownership,
          enabled: selection.enabled,
        }
      : null;
  return {
    ...context,
    selection,
    returnMsoId,
    target,
  };
}

/** Carry browsing scope through links without changing the authenticated shop. */
export function localAdminVisitUrl(page, address, selectionOverride = null) {
  const context = resolveAdminContext(page);
  if (!context) return address;
  const url = new URL(address, window.location.origin);
  const prefix = `/shop/${context.originShopId}/bayhive-admin`;
  if (
    url.origin !== window.location.origin ||
    !(url.pathname === prefix || url.pathname.startsWith(`${prefix}/`))
  )
    return address;
  const selection = selectionOverride || readSelection(url, context.selection);
  if (selectionOverride && selectionKey(selectionOverride) !== selectionKey(context.selection)) {
    for (const key of [
      'account',
      'page',
      'perPage',
      'admin_detail',
      'admin_view',
      'admin_detail_params',
    ])
      url.searchParams.delete(key);
  }
  const currentUrl = new URL(page.url || window.location.href, window.location.origin);
  if (url.pathname !== currentUrl.pathname) {
    url.searchParams.delete('admin_detail');
    url.searchParams.delete('admin_view');
    url.searchParams.delete('admin_detail_params');
  }
  writeSelection(url, selection);
  if (!url.searchParams.has('admin_return_mso') && context.returnMsoId)
    url.searchParams.set('admin_return_mso', context.returnMsoId);
  if (selection.type !== 'shop') url.searchParams.delete('admin_return_mso');
  const targetId = selection.type === 'shop' ? selection.id : context.originShopId;
  const pathTarget = url.pathname.match(/\/shops\/(\d+)$/)?.[1];
  url.searchParams.delete('admin_account');
  if (url.searchParams.has('target_shop')) url.searchParams.set('target_shop', targetId);
  if (pathTarget) url.pathname = `${prefix}/shops/${targetId}`;
  if (url.pathname.endsWith('/workspaces/marketing/setup'))
    url.searchParams.set('target_shop', targetId);
  // Keep links on the canonical Control Panel route, including copied URLs.
  if (
    [`${prefix}/workspaces/accounts/control-panel`, prefix, `${prefix}/`].includes(url.pathname)
  ) {
    url.pathname = `${prefix}/shops/${targetId}`;
    url.searchParams.delete('target_shop');
  }
  return url;
}
